/**
 * Generic Drill-Down Component v2
 *
 * Gold-standard data drilling experience inspired by Monutrack:
 * - Tab-based pre-aggregated dimension views
 * - KPI cards with dimension counts (clickable for chart)
 * - Smart filter limits (top 50 by value)
 * - First-class comparison toggle with summary panel
 * - Percentage progress bars in tables
 * - Time series chart with smart granularity
 * - CSV export
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useDrillDown,
  useDrillDownComparison,
  useDrillDownFiltering,
} from './useDrillDown';
import { DREControlsPanel, type ComparisonMode } from '../components';

import type {
  DrillDownRow,
  GroupedRow,
  DimensionTab,
  ComparisonDates,
  DrillDownGranularity,
  ChartType,
  PeriodDates,
} from './types';

import { DEFAULT_DIMENSION_TABS, METRIC_LABELS } from './constants';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatDate,
  calculateComparisonDates,
} from './utils';

import { ChartTypeToggle } from './components/ChartTypeToggle';
import { KPICard } from './components/KPICard';
import { ComparisonSummary } from './components/ComparisonSummary';
import { DimensionTabs } from './components/DimensionTabs';
import { SmartFilter } from './components/SmartFilter';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { GroupedTable } from './components/GroupedTable';
import { TransactionsTable } from './components/TransactionsTable';
import { BottomActionSheet, type ActionItem } from './components/BottomActionSheet';
import { FloatingActionButton } from './components/FloatingActionButton';

interface GenericDrillDownProps {
  data: DrillDownRow[];
  title: string;
  subtitle?: string;
  comparisonData?: DrillDownRow[] | null;
  onBack?: () => void;
  backLabel?: string;
  onComparisonChange?: (dates: ComparisonDates | null) => void;
  onPeriodChange?: (dates: PeriodDates) => void;
  startDate: Date;
  endDate: Date;
  comparisonStartDate?: Date;
  comparisonEndDate?: Date;
  /** Custom dimension tabs (optional - auto-detected if not provided) */
  dimensionTabs?: DimensionTab[];
  /** Time aggregation granularity (passed from DRE page) */
  granularity?: DrillDownGranularity;
  /** Callback when granularity changes */
  onGranularityChange?: (granularity: DrillDownGranularity) => void;
  /** Chart type: bar or line */
  chartType?: ChartType;
  /** Callback when chart type changes */
  onChartTypeChange?: (chartType: ChartType) => void;
  /** Selected dimension keys for chart breakdown (multi-select) */
  selectedDimensions?: string[];
  /** Callback when dimension selection changes (toggle) */
  onDimensionToggle?: (dimensionKey: string) => void;
  /** Callback to clear all selected dimensions */
  onClearDimensions?: () => void;
}

export function GenericDrillDown({
  data,
  title,
  subtitle,
  comparisonData = null,
  onBack,
  backLabel = 'Voltar',
  onComparisonChange,
  onPeriodChange,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  dimensionTabs: customTabs,
  granularity = 'monthly',
  onGranularityChange,
  chartType = 'bar',
  onChartTypeChange,
  selectedDimensions = [],
  onDimensionToggle,
  onClearDimensions,
}: GenericDrillDownProps): JSX.Element {
  // Core drill-down state
  const drillDown = useDrillDown(data);

  // Filter comparison data with the same filters as current data
  // Only filter if comparisonData exists (comparison mode enabled)
  const { filteredData: filteredComparisonData } = useDrillDownFiltering(
    comparisonData || [],
    drillDown.filters
  );

  // Effective comparison data: filtered version of comparison data (null if no comparison)
  const effectiveComparisonData = comparisonData ? filteredComparisonData : null;

  // Comparison state - use ComparisonMode (same as DRE page)
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(
    comparisonData ? 'previous_period' : 'none'
  );
  const comparisonEnabled = comparisonMode !== 'none';

  // UI state
  const [activeTab, setActiveTab] = useState('supplier');

  // Build available tabs based on detected dimensions
  const availableTabs = useMemo(() => {
    if (customTabs) return customTabs;

    const detected = drillDown.allDimensions.map(d => d.key);
    return DEFAULT_DIMENSION_TABS.filter(tab =>
      tab.key === null || // Always include transactions
      detected.includes(tab.key)
    );
  }, [customTabs, drillDown.allDimensions]);

  // Dimension counts for tabs
  const dimensionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    drillDown.allDimensions.forEach(dim => {
      counts[dim.key] = dim.valueCount;
    });
    return counts;
  }, [drillDown.allDimensions]);

  // Current active dimension
  const activeTabConfig = availableTabs.find(t => t.id === activeTab) || availableTabs[0];
  const activeDimensionKey = activeTabConfig?.key;

  // Update groupByKey when tab changes
  useEffect(() => {
    if (activeDimensionKey !== null) {
      drillDown.setGroupByKey(activeDimensionKey);
    } else {
      drillDown.setGroupByKey(null);
    }
  }, [activeDimensionKey, drillDown]);

  // Comparison hook - use filtered comparison data
  const { comparison } = useDrillDownComparison(
    drillDown.filteredData,
    effectiveComparisonData,
    activeDimensionKey
  );

  // Period labels
  const periodLabels = useMemo(() => {
    const format = (d: Date): string => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
    return {
      current: `${format(startDate)} - ${format(endDate)}`,
      comparison: comparisonStartDate && comparisonEndDate
        ? `${format(comparisonStartDate)} - ${format(comparisonEndDate)}`
        : '',
    };
  }, [startDate, endDate, comparisonStartDate, comparisonEndDate]);

  // Handle comparison mode change - maps ComparisonMode to ComparisonDates
  const handleComparisonModeChange = useCallback((mode: ComparisonMode) => {
    setComparisonMode(mode);
    if (mode === 'none') {
      onComparisonChange?.(null);
    } else {
      // Map ComparisonMode to the preset format used by calculateComparisonDates
      const presetMap = {
        'previous_period': 'previous_period',
        'same_period_last_year': 'previous_year',
      } as const;
      const dates = calculateComparisonDates(presetMap[mode], startDate, endDate);
      onComparisonChange?.(dates);
    }
  }, [startDate, endDate, onComparisonChange]);

  // Handle row click (drill into)
  const handleRowClick = useCallback((groupValue: string) => {
    if (!activeDimensionKey) return;
    drillDown.setFilter(activeDimensionKey, [groupValue]);
    setActiveTab('transactions');
  }, [activeDimensionKey, drillDown]);

  // Export to CSV
  const handleExport = useCallback(() => {
    const rows = activeDimensionKey ? drillDown.groupedData : drillDown.filteredData;
    if (rows.length === 0) return;

    let csv: string;
    if (activeDimensionKey) {
      const dim = drillDown.allDimensions.find(d => d.key === activeDimensionKey);
      const header = [dim?.label || activeDimensionKey, 'Quantidade', 'Total', '%'];
      const dataRows = (rows as GroupedRow[]).map(r => [
        r.dimensionValue,
        r.count,
        r.total.toFixed(2),
        r.percentage.toFixed(1),
      ]);
      csv = [header.join(';'), ...dataRows.map(r => r.join(';'))].join('\n');
    } else {
      const keys = Object.keys(rows[0]).filter(k => k !== 'Valor' && k !== 'Data');
      const header = ['Data', ...keys, 'Valor'];
      const dataRows = (rows as DrillDownRow[]).map(r => [
        formatDate(r.Data as string),
        ...keys.map(k => r[k] ?? ''),
        r.Valor?.toFixed(2) ?? '',
      ]);
      csv = [header.join(';'), ...dataRows.map(r => r.join(';'))].join('\n');
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [activeDimensionKey, drillDown, title]);

  // Mobile action sheet state
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  // Mobile action handlers
  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${title} - Monutrack`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a area de transferencia');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.error('Erro ao compartilhar');
      }
    }
  }, [title]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiado para a area de transferencia');
    } catch {
      toast.error('Erro ao copiar link');
    }
  }, []);

  // Mobile action items
  const mobileActions: ActionItem[] = useMemo(() => {
    const actions: ActionItem[] = [
      {
        label: 'Exportar CSV',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        ),
        onClick: handleExport,
      },
      {
        label: 'Compartilhar',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        ),
        onClick: handleShare,
      },
      {
        label: 'Copiar Link',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        ),
        onClick: handleCopyLink,
      },
    ];

    // Add back button if onBack is provided
    if (onBack) {
      actions.push({
        label: backLabel,
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        ),
        onClick: onBack,
      });
    }

    return actions;
  }, [handleExport, handleShare, handleCopyLink, onBack, backLabel]);

  // Format date for display
  const formatDateForInput = (d: Date): string => d.toISOString().split('T')[0];

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', padding: '1.5rem' }}>
      {/* Header - Title + Back Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, color: '#ffffff' }}>{title}</h1>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#2A2D34',
              color: '#D2FA52',
              border: '1px solid #3A3D44',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            ← {backLabel}
          </button>
        )}
      </div>

      {subtitle && (
        <p style={{ color: '#838998', marginBottom: '1.5rem' }}>{subtitle}</p>
      )}

      {/* Controls Panel - Shared Component */}
      <DREControlsPanel
        startDate={formatDateForInput(startDate)}
        endDate={formatDateForInput(endDate)}
        onPeriodChange={onPeriodChange || (() => {})}
        showRefreshButton={false}
        comparisonMode={comparisonMode}
        onComparisonModeChange={handleComparisonModeChange}
        granularity={granularity}
        onGranularityChange={onGranularityChange || (() => {})}
        showPeriod={!!onPeriodChange}
        showComparison={!!onComparisonChange}
        showGranularity={!!onGranularityChange}
      />

      {/* Comparison Summary */}
      {comparisonEnabled && comparisonData && comparison && (
        <ComparisonSummary
          currentTotal={drillDown.totals.totalValue}
          comparisonTotal={comparison.summary.comparisonTotal}
          currentCount={drillDown.totals.totalCount}
          comparisonCount={effectiveComparisonData?.length || 0}
          currentLabel={periodLabels.current}
          comparisonLabel={periodLabels.comparison}
        />
      )}

      {/* KPI Cards - All are multi-selectable
          Responsive Grid:
          mobile (<640px): 2 columns
          sm (≥640px): 3 columns (tablet)
          lg (≥1024px): 4 columns (desktop)
          xl (≥1280px): 6 columns (large desktop)
      */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 xl:gap-6 mb-6 items-start">
        <KPICard
          label="Total"
          value={formatCurrency(drillDown.totals.totalValue)}
          change={comparison ? comparison.summary.totalDeltaPercentage : null}
          icon=""
          subtitle={selectedDimensions.includes('total') ? 'no grafico' : 'clique p/ ver'}
          active={selectedDimensions.includes('total')}
          onClick={() => onDimensionToggle?.('total')}
        />
        <KPICard
          label="Registros"
          value={formatNumber(drillDown.totals.totalCount)}
          icon=""
          subtitle={selectedDimensions.includes('count') ? 'no grafico' : 'clique p/ ver'}
          active={selectedDimensions.includes('count')}
          onClick={() => onDimensionToggle?.('count')}
        />
        {drillDown.allDimensions.slice(0, 4).map(dim => (
          <KPICard
            key={dim.key}
            label={dim.label}
            value={`${dim.valueCount}`}
            subtitle={selectedDimensions.includes(dim.key) ? 'no grafico' : 'unicos • clique p/ ver'}
            icon={dim.key === 'Fornecedor' ? '' : dim.key === 'Cliente' ? '' : dim.key === 'Job' ? '' : ''}
            active={selectedDimensions.includes(dim.key)}
            onClick={() => onDimensionToggle?.(dim.key)}
          />
        ))}
      </div>

      {/* Selected Metrics/Dimensions Indicator */}
      {selectedDimensions.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: 'rgba(210, 250, 82, 0.15)',
          borderRadius: '8px',
          fontSize: '0.875rem',
        }}>
          <span style={{ color: '#D2FA52', fontWeight: 500 }}>
            Exibindo: {selectedDimensions.map(d => METRIC_LABELS[d] || d).join(', ')} por periodo
          </span>
          <button
            onClick={onClearDimensions}
            style={{
              padding: '0.25rem 0.5rem',
              background: '#D2FA52',
              color: '#232429',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            Limpar selecao
          </button>
        </div>
      )}

      {/* Time Series Chart with Controls */}
      <div style={{ marginBottom: '1.5rem' }}>
        {/* Chart Controls Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.5rem',
        }}>
          {onChartTypeChange && (
            <ChartTypeToggle
              value={chartType}
              onChange={onChartTypeChange}
            />
          )}
        </div>
        <TimeSeriesChart
          data={drillDown.filteredData}
          comparisonData={effectiveComparisonData}
          startDate={startDate}
          endDate={endDate}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
          granularity={granularity}
          chartType={chartType}
          selectedDimensions={selectedDimensions}
        />
      </div>

      {/* Dimension Tabs */}
      <DimensionTabs
        tabs={availableTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        dimensionCounts={dimensionCounts}
      />

      {/* Smart Filters */}
      <SmartFilter
        dimensions={drillDown.allDimensions}
        data={data}
        activeFilters={drillDown.filters}
        onFilterChange={drillDown.setFilter}
        onClearAll={drillDown.clearAllFilters}
        searchQuery={drillDown.textSearch}
        onSearchChange={drillDown.setTextSearch}
      />

      {/* Action Bar */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <button
          onClick={handleExport}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '1px solid #3A3D44',
            background: '#2A2D34',
            color: '#ffffff',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          📥 Exportar CSV
        </button>
        {drillDown.filterSummary.isFiltered && (
          <span style={{ fontSize: '0.875rem', color: '#838998' }}>
            Mostrando {formatNumber(drillDown.filterSummary.filtered)} de {formatNumber(drillDown.filterSummary.total)} ({formatPercent(drillDown.filterSummary.percentage)})
          </span>
        )}
      </div>

      {/* Data Display */}
      {activeDimensionKey ? (
        <GroupedTable
          data={drillDown.groupedData}
          dimensionLabel={drillDown.allDimensions.find(d => d.key === activeDimensionKey)?.label || activeDimensionKey}
          dimensionKey={activeDimensionKey}
          comparison={comparisonEnabled ? comparison : null}
          onRowClick={handleRowClick}
          sortBy={drillDown.sortBy}
          sortOrder={drillDown.sortOrder}
          onSort={drillDown.toggleSort}
        />
      ) : (
        <TransactionsTable rows={drillDown.filteredData} />
      )}

      {/* Mobile FAB and Action Sheet */}
      <FloatingActionButton
        onClick={() => setIsActionSheetOpen(true)}
        ariaLabel="Abrir menu de acoes"
      />
      <BottomActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        title="Acoes"
        actions={mobileActions}
      />
    </div>
  );
}

export default GenericDrillDown;
